import { createLayer } from "@/api/hooks/layer";
import { FeatureGate } from "@/components/molecules/FeatureGate/FeatureGate";
import { FeatureHubGuard } from "@/featureFlags";
import type { LayerWithParameterUnit } from "@/layers/utility/createLayerObject";
import CompleteThreshold from "@/overlay/components/CommonParts/Threshold/CompleteThreshold";
import "@/overlay/components/CommonParts/Threshold/CompleteThreshold.scss";
import type { ThresholdColor } from "@/overlay/components/CommonParts/Threshold/types";
import { Button, ButtonBar } from "@/overlay/components/CommonParts/buttons";
import { CreateAlertModal } from "@/overlay/components/LayerStackWindow/AlertLayer";
import { InitDateField } from "@/overlay/components/LayerStackWindow/InitDateField";
import { CalibratedToggle } from "@/overlay/components/LayerStackWindow/LayerFormElements/CalibratedToggle";
import { PoiInputFields } from "@/overlay/components/LayerStackWindow/PoiLayerPanel/PoiInputFields";
import { ModelAndWeatherParameterForm } from "@/overlay/components/ModelAndWeatherParameterForm";
import type { ParameterEditCallback } from "@/overlay/components/WeatherParameterForm";
import { FormField } from "@/overlay/components/form-field";
import { WindowKind, instanceId, useDesktop } from "@/overlay/components/window";
import { useAppDispatch } from "@/reducer";
import { removeLayer } from "@/reducer/MapsState";
import type { ColoringOptions, GenericPoiOptions, IconData } from "@/reducer/client-models";
import type { GenericPoiLayer as PoiLayer } from "@/reducer/client-models/PoiLayer";
import type { PoiDataGrid } from "@/threads/DataProcessingThread/types";
import type { MapId } from "@/types/type-utils";
import { hasCalibratedAvailable } from "@/utility/calibratedAvailabilityMap";
import { t } from "@lingui/core/macro";
import { Trans } from "@lingui/react/macro";
import Logger from "@mm/logging";
import type { AlertLayerCreate } from "@mm/metx-workbench.meteomatics.com";
import { useEffect, useState } from "react";
import type { DraggableItemIdentifier } from "../../CommonParts/SortableList";

const PoiLayerPanel = (props: {
  mapId: MapId;
  poiOptions: GenericPoiOptions;
  onChangeItem: (newItem: IconData) => void;
  onChangePoiOptions: (options: Partial<GenericPoiOptions>) => void;
  onChangeColoringOptions: (coloringOptions: ColoringOptions | undefined) => void;
  onChangeLayerProps: (propsValue: Partial<PoiLayer>) => void;
  onEditParameter: ParameterEditCallback;
  onAddParameter: () => void;
  layer: PoiLayer;
  nextLayerIndex: number;
}) => {
  const desktop = useDesktop();
  const dispatch = useAppDispatch();
  const logger = Logger.fromFilename(__filename);
  const [item, setItem] = useState<IconData>(props.poiOptions.item);
  const [coloringOptions, setColoringOptions] = useState<ColoringOptions | undefined>(props.poiOptions.coloringOptions);
  const [isCreateAlertOpen, setIsCreateAlertOpen] = useState(false);
  const poiParamUnit = props.layer.parameter_unit;

  async function createAlertLayer(title: string, thresholds: ThresholdColor[]) {
    const alertLayer = {
      index: props.nextLayerIndex,
      opacity: 1,
      show: true,
      calibrated: false,
      vertical_interpolation: null,
      experimental: false,
      label: title.trim(),
      custom_options: { reference: "" },
      kind: "AlertLayerDescription" as const,
      alert_layer_draft: {
        title,
        parameterName: poiParamUnit,
        sourceModel: props.layer.model,
        locations: item.coords
          .filter(
            (location) => location.lat != null && location.lat !== "" && location.lon != null && location.lon !== "",
          )
          .map(({ lat, lon, name, label }) => ({
            lat: Number(lat),
            lon: Number(lon),
            ...(name ?? label ? { label: name ?? label } : {}),
          })),
        thresholds,
      },
    } as AlertLayerCreate;

    try {
      await dispatch(
        createLayer({
          mapId: props.mapId.map,
          profileId: props.mapId.profile,
          data: alertLayer,
        }),
      ).unwrap();

      desktop.dispatch({
        type: "confirmationWindow",
        windowId: instanceId(WindowKind.ConfirmationWindow),
        customProps: {
          title: t`Remove POI layer?`,
          message: t`The alert layer was created successfully. The alert layer is independent from the POI layer, so later changes to POI markers or thresholds will not affect it. Do you want to delete the POI layer now?`,
          confirmLabel: t`Yes`,
          cancelLabel: t`No`,
          promptType: "warning",
          callback: (confirmed: boolean) => {
            if (!confirmed) {
              return;
            }

            dispatch(removeLayer({ mapId: props.mapId.map, layerId: props.layer.id }));
          },
        },
      });
    } catch (error) {
      logger.error("Failed to create Alert Layer from POI layer.", error);
      throw error;
    }
  }
  useEffect(() => {
    setColoringOptions(props.poiOptions.coloringOptions);
    setItem(props.poiOptions.item);
  }, [props.poiOptions]);

  function triggerColoringOptionsOnChange(propsValue: Partial<ColoringOptions>) {
    if (coloringOptions) {
      const options: ColoringOptions = { ...coloringOptions, ...propsValue };
      setColoringOptions(options);
      props.onChangeColoringOptions(options);
    }
  }

  function triggerOnChange(propsValue: Partial<IconData>) {
    const newItem = { ...item, ...propsValue };
    setItem(newItem);
    props.onChangeItem(newItem);
  }

  function handleToggleLabels(hideLabels: boolean) {
    props.onChangePoiOptions({ hideLabels });
    if (!hideLabels) {
      if (!poiParamUnit) {
        props.onAddParameter();
      }
    }
  }

  function handleRemoveParameterAndThresholds() {
    setColoringOptions(undefined);
    props.onChangeColoringOptions(undefined);
  }

  function handleEditPoints() {
    desktop.dispatch({
      type: "confirmationWindow",
      windowId: instanceId(WindowKind.PoiManagerWindow),
      customProps: {
        onUpdate: (data: PoiDataGrid) => {
          triggerOnChange({ coords: data });
        },
        mapId: props.mapId.map,
        initialData: item.coords,
      },
    });
  }

  function moveThresholdIdx(dragStart: DraggableItemIdentifier, dropTarget: DraggableItemIdentifier) {
    if (!coloringOptions?.thresholds) {
      console.error("Threshold orders were swapped but no data is available");
      return;
    }
    const copyOfThresholds = [...coloringOptions.thresholds];

    const fromIdx = dragStart.index;
    let toIdx = dropTarget.index;
    if (toIdx >= copyOfThresholds.length) {
      toIdx = copyOfThresholds.length - 1;
    }
    if (toIdx < 0) {
      toIdx = 0;
    }

    const targetThreshold = copyOfThresholds.splice(fromIdx, 1)[0];
    copyOfThresholds.splice(toIdx, 0, targetThreshold);

    triggerColoringOptionsOnChange({ thresholds: copyOfThresholds });
  }

  return (
    <div>
      <PoiInputFields
        item={item}
        hideLabels={!poiParamUnit || props.layer.poiOptions.hideLabels}
        onInputChange={triggerOnChange}
        onToggleLabels={handleToggleLabels}
      />
      <FormField label={t`Points`}>
        <Button icon="my_location" onClick={handleEditPoints}>
          <Trans>Manage</Trans>
        </Button>
      </FormField>

      {!poiParamUnit && (
        <FormField label={t`Data`} noSemanticLinking>
          <Button icon="add" onClick={props.onAddParameter}>
            <Trans>Add Parameter</Trans>
          </Button>
        </FormField>
      )}

      {poiParamUnit && (
        <>
          <ModelAndWeatherParameterForm
            layer={props.layer as LayerWithParameterUnit}
            mapId={props.mapId}
            onEditParameter={props.onEditParameter}
            onRemoveParameter={handleRemoveParameterAndThresholds}
            includeStationModels
          />
          {hasCalibratedAvailable(props.layer.parameter_unit, props.layer.model) && (
            <CalibratedToggle value={!!props.layer.calibrated} change={props.onChangeLayerProps} />
          )}
          <FeatureHubGuard flag="api_url_suffix">
            <InitDateField
              label={t`API URL suffix`}
              defaultValue={props.poiOptions.apiUrlSuffix ?? ""}
              onChange={(apiUrlSuffix) => props.onChangePoiOptions({ apiUrlSuffix })}
            />
          </FeatureHubGuard>
          <CompleteThreshold
            onMoveIndex={moveThresholdIdx}
            updateThresholds={(newThresholds: ThresholdColor[]) =>
              triggerColoringOptionsOnChange({
                ...coloringOptions,
                thresholds: newThresholds,
              })
            }
            updateAdvancedThresholds={(newThresholds) =>
              triggerColoringOptionsOnChange({
                ...coloringOptions,
                advancedThresholds: newThresholds,
              })
            }
            thresholds={coloringOptions?.thresholds || []}
            advancedThresholds={coloringOptions?.advancedThresholds || []}
            enableAdvancedThreshold
            isPoi={true}
          />
          <FeatureHubGuard flag="feature.alert_layer">
            <FeatureGate path={"tools.alert.create"}>
              <>
                {(coloringOptions?.thresholds.length || 0) > 0 && (
                  <ButtonBar className="layer-stack-thresholds">
                    <Button primary icon="notifications" type="button" onClick={() => setIsCreateAlertOpen(true)}>
                      <Trans>Create Alert</Trans>
                    </Button>
                  </ButtonBar>
                )}
                <CreateAlertModal
                  isOpen={isCreateAlertOpen}
                  onClose={() => setIsCreateAlertOpen(false)}
                  locations={item.coords}
                  thresholds={coloringOptions?.thresholds ?? []}
                  parameterUnit={poiParamUnit}
                  defaultTitle={props.layer.label ?? ""}
                  onCreateAlertLayer={createAlertLayer}
                />
              </>
            </FeatureGate>
          </FeatureHubGuard>
        </>
      )}
    </div>
  );
};

export default PoiLayerPanel;
